import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Spinner,
  Badge, SimpleGrid, Divider, Icon, Progress,
  Alert, AlertIcon,
} from '@chakra-ui/react';
import { FiRefreshCw, FiCpu, FiCheckCircle, FiXCircle, FiWifi } from 'react-icons/fi';

const API_BASE = '/api/v1';
const POLL_INTERVAL_MS = 10000;

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

const cpuColor = (percent) => {
  if (percent >= 85) return 'red';
  if (percent >= 60) return 'yellow';
  return 'green';
};

const formatBytesPerSec = (bytesPerSec) => {
  if (bytesPerSec == null) return '—';
  if (bytesPerSec < 1024) return `${bytesPerSec.toFixed(0)} B/s`;
  if (bytesPerSec < 1024 * 1024) return `${(bytesPerSec / 1024).toFixed(1)} KB/s`;
  return `${(bytesPerSec / (1024 * 1024)).toFixed(1)} MB/s`;
};

function Nodes() {
  const [nodes, setNodes] = useState([]);
  const [networkRates, setNetworkRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastSampleRef = useRef({});

  const fetchNodes = useCallback(() => {
    fetch(`${API_BASE}/k8s/nodes/`, { headers: getAuthHeaders() })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        const fetchedNodes = data.nodes || [];
        setNodes(fetchedNodes);
        setLoading(false);
        setError(null);

        const now = Date.now();
        const rates = {};
        fetchedNodes.forEach((node) => {
          const { network_rx_bytes: rx, network_tx_bytes: tx } = node.usage || {};
          const prev = lastSampleRef.current[node.name];
          if (prev && rx != null && tx != null) {
            const elapsedSeconds = (now - prev.timestamp) / 1000;
            if (elapsedSeconds > 0) {
              rates[node.name] = {
                rxRate: Math.max(0, (rx - prev.rx) / elapsedSeconds),
                txRate: Math.max(0, (tx - prev.tx) / elapsedSeconds),
              };
            }
          }
          if (rx != null && tx != null) {
            lastSampleRef.current[node.name] = { rx, tx, timestamp: now };
          }
        });
        setNetworkRates((current) => ({ ...current, ...rates }));
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchNodes();
    const interval = setInterval(fetchNodes, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchNodes]);

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="gray.500" /></Box>;
  if (error) return <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Nodes</Heading>
          <Text color="gray.500">{nodes.length} cluster nodes</Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="gray" onClick={fetchNodes}>Refresh</Button>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        {nodes.map((node) => {
          const ready = node.conditions?.Ready === 'True';
          return (
            <Box key={node.name} bg="white" p={6} borderRadius="lg" boxShadow="md">
              <HStack mb={4}>
                <Icon as={FiCpu} boxSize={6} color={ready ? 'green.500' : 'red.500'} />
                <Heading size="md" isTruncated>{node.name}</Heading>
                <Badge colorScheme={ready ? 'green' : 'red'}>
                  <Icon as={ready ? FiCheckCircle : FiXCircle} mr={1} />
                  {ready ? 'Ready' : 'Not Ready'}
                </Badge>
              </HStack>
              <Divider mb={4} />

              <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={2}>Live Usage</Text>
              <VStack align="stretch" spacing={2} mb={4}>
                <Box>
                  <HStack justify="space-between" mb={1}>
                    <HStack spacing={1}>
                      <Icon as={FiCpu} boxSize={3.5} color="gray.400" />
                      <Text fontSize="xs" color="gray.400">CPU</Text>
                    </HStack>
                    <Text fontSize="xs" fontWeight="bold">
                      {node.usage?.cpu_percent != null ? `${node.usage.cpu_percent.toFixed(1)}%` : '—'}
                    </Text>
                  </HStack>
                  <Progress
                    value={node.usage?.cpu_percent ?? 0}
                    size="sm"
                    borderRadius="full"
                    colorScheme={node.usage?.cpu_percent != null ? cpuColor(node.usage.cpu_percent) : 'gray'}
                  />
                </Box>
                <HStack spacing={1}>
                  <Icon as={FiWifi} boxSize={3.5} color="gray.400" />
                  <Text fontSize="xs" color="gray.400">Network</Text>
                  <Text fontSize="xs" fontWeight="bold">
                    ↓ {formatBytesPerSec(networkRates[node.name]?.rxRate)}
                  </Text>
                  <Text fontSize="xs" fontWeight="bold">
                    ↑ {formatBytesPerSec(networkRates[node.name]?.txRate)}
                  </Text>
                </HStack>
              </VStack>

              <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={2}>Capacity</Text>
              <HStack spacing={4} mb={4}>
                <VStack align="start" spacing={0}>
                  <Text fontSize="xs" color="gray.400">CPU</Text>
                  <Text fontWeight="bold">{node.capacity?.cpu || '—'}</Text>
                </VStack>
                <VStack align="start" spacing={0}>
                  <Text fontSize="xs" color="gray.400">Memory</Text>
                  <Text fontWeight="bold">{node.capacity?.memory || '—'}</Text>
                </VStack>
                <VStack align="start" spacing={0}>
                  <Text fontSize="xs" color="gray.400">Pods</Text>
                  <Text fontWeight="bold">{node.capacity?.pods || '—'}</Text>
                </VStack>
              </HStack>

              <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={2}>Allocatable</Text>
              <HStack spacing={4}>
                <VStack align="start" spacing={0}>
                  <Text fontSize="xs" color="gray.400">CPU</Text>
                  <Text fontWeight="bold">{node.allocatable?.cpu || '—'}</Text>
                </VStack>
                <VStack align="start" spacing={0}>
                  <Text fontSize="xs" color="gray.400">Memory</Text>
                  <Text fontWeight="bold">{node.allocatable?.memory || '—'}</Text>
                </VStack>
                <VStack align="start" spacing={0}>
                  <Text fontSize="xs" color="gray.400">Pods</Text>
                  <Text fontWeight="bold">{node.allocatable?.pods || '—'}</Text>
                </VStack>
              </HStack>

              {Object.keys(node.conditions || {}).length > 0 && (
                <>
                  <Divider my={4} />
                  <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={2}>Conditions</Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {Object.entries(node.conditions).map(([k, v]) => (
                      <Badge
                        key={k}
                        colorScheme={v === 'True' && k === 'Ready' ? 'green' : v === 'False' ? 'green' : 'red'}
                        fontSize="xs"
                      >
                        {k}: {v}
                      </Badge>
                    ))}
                  </HStack>
                </>
              )}
            </Box>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}

export default Nodes;
